<template>
  <div 
    class="group relative bg-white dark:bg-slate-900 rounded-lg shadow-md overflow-hidden transition-shadow hover:shadow-lg cursor-pointer"
    @click="handleCardClick"
  >
    <!-- Favorite Button (Top-Right of Card) -->
    <div class="absolute top-3 right-3 z-20">
      <FavoriteButton 
        v-if="vehicle.id" 
        :vehicle-id="vehicle.id" 
        class="promo-card-favorite-button" 
      />
    </div>

    <!-- Vehicle Image Section -->
    <div class="relative aspect-w-16 aspect-h-9">
      <img
        v-if="vehicle.image || vehicle.thumb"
        :src="vehicle.image || vehicle.thumb"
        :alt="vehicle.title || 'Vehicle image'"
        class="w-full h-full object-cover"
        @load="onImageLoad"
        @error="onImageError"
        :class="{ 'opacity-0': !imageLoaded || imageError }"
      />
      <!-- Loading/Error Placeholder -->
      <div
        v-if="!imageLoaded || imageError"
        class="absolute inset-0 flex items-center justify-center bg-slate-200 dark:bg-slate-800"
      >
        <div v-if="!imageLoaded && !imageError" class="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        <svg v-if="imageError" xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>

      <!-- Top-Left Badges Container -->
      <div class="absolute top-2 left-2 sm:top-3 sm:left-3 z-10 flex flex-col items-start gap-y-1.5">
        <!-- Large Promo Badge (Image or Text) -->
        <img v-if="vehicle.promoBadgeUrl" :src="vehicle.promoBadgeUrl" alt="Promo" class="h-14 w-auto sm:h-16 md:h-20 shadow-lg rounded-sm">
        <span v-else-if="vehicle.promoText" class="bg-primary text-white text-xs sm:text-sm font-semibold px-2 py-1 rounded-md shadow-lg">
          {{ vehicle.promoText }}
        </span>

        <!-- Condition Pill Badge (New/Demo/Used) -->
        <span 
          v-if="conditionBadge.text" 
          :class="[
            'text-xs font-semibold px-2.5 py-0.5 m-4 rounded-full shadow',
            conditionBadge.class
          ]"
        >
          {{ conditionBadge.text }}
        </span>
      </div>

      <!-- Photo Count (Bottom-Left) -->
      <div v-if="vehicle.photoCount" class="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 z-10 bg-black/50 text-white text-xs sm:text-sm px-2 py-1 rounded-md flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        {{ vehicle.photoCount }}
      </div>
    </div>

    <!-- Content Section -->
    <div class="p-4">
      <div class="mb-3">
        <NuxtLink :to="vehicleLink" @click.stop class="block hover:no-underline">
          <h3 class="text-lg sm:text-xl font-bold text-slate-800 dark:text-white mt-1 hover:text-primary dark:hover:text-primary transition-colors">
            {{ vehicle.make?.displayValue?.[0] || vehicle.make }} {{ vehicle.model?.displayValue?.[0] || vehicle.model }}
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400 line-clamp-1 hover:text-primary dark:hover:text-primary transition-colors">
            {{ vehicle.title }}
          </p>
        </NuxtLink>
      </div>

      <!-- Pricing Section -->
      <div class="flex justify-between items-end mb-4">
        <div class="flex flex-col items-start">
          <span class="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white leading-none">
            ${{ (vehicle.price || vehicle.egc_price || 0).toLocaleString() }}
          </span>
          <span v-if="vehicle.price" class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Drive away<sup class="text-red-500 ml-px">*</sup>
          </span>
          <span v-else-if="vehicle.egc_price" class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Excl. Govt. Charges
          </span>
        </div>
        <div v-if="vehicle.perweek" class="text-right">
          <span class="text-lg sm:text-xl font-semibold text-blue-600 dark:text-blue-400">
            ${{ formatPerWeek(vehicle.perweek) }}p/w
          </span>
          <p class="text-xs text-slate-500 dark:text-slate-400">Est. repayments</p>
        </div>
      </div>

      <!-- Specs Section -->
      <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-slate-700 dark:text-slate-300 border-t border-slate-200 dark:border-slate-700 pt-4">
        <div class="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-slate-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 2a2 2 0 00-2 2v1H6a1 1 0 000 2h1v5H6a1 1 0 100 2h1v1a2 2 0 002 2h4a2 2 0 002-2v-1h1a1 1 0 100-2h-1V7h1a1 1 0 100-2h-2V4a2 2 0 00-2-2H9zm-1 4H7v5h2V6zm2 0h2v5h-2V6z" clip-rule="evenodd" /> <path d="M5 16.33A2.39 2.39 0 013 15V8.48a2.43 2.43 0 011-1.98L6.79 4.6A2.4 2.4 0 018.69 4h2.62a2.4 2.4 0 011.9.6l2.79 1.9a2.43 2.43 0 011 1.98V15a2.39 2.39 0 01-2 2.33H5zm11-1.33a.39.39 0 00.33-.35V8.48a.43.43 0 00-.18-.35L13.36 6.2a.41.41 0 00-.31-.1H7a.41.41 0 00-.31.1L3.82 7.78a.43.43 0 00-.18.35V15a.39.39 0 00.33.35H16z"/>
          </svg>
          <span class="truncate">{{ vehicle.body?.displayValue?.[0] || 'N/A' }} / {{ vehicle.seats?.displayValue?.[0] || 'N/A' }} seats</span>
        </div>
        <div class="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-slate-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 4a1 1 0 10-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4z" />
          </svg>
          <span class="truncate">{{ getFuelInfo(vehicle) }}</span>
        </div>
        <div class="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-slate-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.566.379-1.566 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.566 2.6 1.566 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.566-.379 1.566-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd" />
          </svg>
          <span class="truncate">{{ vehicle.transmission?.displayValue?.[0] || 'N/A' }}</span>
        </div>
        <div class="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-slate-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
          <span class="truncate">{{ vehicle.registrationStatus || (vehicle.kms !== undefined && Number(vehicle.kms) < 1000) ? 'Unregistered' : 'Registered' }}</span>
        </div>
        <div class="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-slate-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
          </svg>
          <span class="truncate">{{ formatKilometers(vehicle.kms) }}</span>
        </div>
        <div class="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-slate-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2ZM8.11803 12.812C8.03703 12.836 7.96103 12.871 7.87603 12.901C7.74503 12.942 7.62703 13 7.50003 13C7.22403 13 7.00003 12.776 7.00003 12.5C7.00003 12.311 7.07903 12.14 7.20403 12.031C7.23903 12.003 7.27003 11.977 7.30103 11.956C7.32103 11.942 7.33803 11.932 7.35303 11.924C7.40703 11.895 7.44903 11.878 7.50003 11.878C7.77603 11.878 8.00003 12.102 8.00003 12.378C8.00003 12.528 7.96103 12.662 7.89103 12.761C7.86703 12.793 7.84803 12.828 7.83503 12.867L8.11803 12.812ZM12.835 12.867C12.848 12.828 12.867 12.793 12.891 12.761C12.961 12.662 13 12.528 13 12.378C13 12.102 12.776 11.878 12.5 11.878C12.449 11.878 12.407 11.895 12.353 11.924C12.338 11.932 12.321 11.942 12.301 11.956C12.27 11.977 12.239 12.003 12.204 12.031C12.079 12.14 12 12.311 12 12.5C12 12.776 12.224 13 12.5 13C12.627 13 12.745 12.942 12.876 12.901C12.961 12.871 13.037 12.836 13.118 12.812L12.835 12.867ZM10.5 10.5C10.5 10.224 10.276 10 10 10C9.72399 10 9.49999 10.224 9.49999 10.5V11H10.5V10.5ZM10.024 6H10C9.72399 6 9.49999 6.22399 9.49999 6.5C9.49999 6.77601 9.72399 7 10 7H11.5C11.776 7 12 6.77601 12 6.5C12 6.22399 11.776 6 11.5 6H10.024Z" />
          </svg>
          <span class="truncate">{{ vehicle.driveAwayPrice ? 'Drive Away' : (vehicle.includesGovtCharges ? 'Incl. Gov Charges' : 'Excl. Gov Charges') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useVehicleQuickView } from '~/layers/base/composables/useVehicleQuickView';
import { useFavoritesStore } from '~/stores/favorites';
import FavoriteButton from '~/layers/base/components/FavoriteButton.vue';
import { ref, onMounted, computed } from 'vue';
import { useRouter } from '#imports'; // Import useRouter for NuxtLink

const { openQuickView } = useVehicleQuickView();
const favoritesStore = useFavoritesStore();
const router = useRouter();

const imageLoaded = ref(false);
const imageError = ref(false);

const props = defineProps({
  vehicle: {
    type: Object as () => any, 
    required: true,
    default: () => ({})
  }
});

// Handle image load/error events
const onImageLoad = () => { imageLoaded.value = true; imageError.value = false; };
const onImageError = () => { imageError.value = true; imageLoaded.value = true; };

onMounted(() => {
  if (props.vehicle.thumb || props.vehicle.image) {
    const img = new Image();
    img.onload = onImageLoad;
    img.onerror = onImageError;
    img.src = props.vehicle.thumb || props.vehicle.image;
    setTimeout(() => { if (img.complete && !imageLoaded.value) onImageLoad(); }, 50);
  } else {
    onImageError();
  }
});

const conditionBadge = computed(() => {
  const conditionValue = props.vehicle.condition?.displayValue?.[0]?.toLowerCase();
  const isNewVehicle = props.vehicle.isNew; // Assuming isNew is a boolean prop

  if (conditionValue === 'new' || isNewVehicle) {
    return { text: 'New', class: 'bg-green-500 text-white' };
  }
  if (conditionValue === 'demo') {
    return { text: 'Demo', class: 'bg-blue-500 text-white' };
  }
  if (conditionValue === 'used') {
    return { text: 'Used', class: 'bg-gray-600 text-white' };
  }
  return { text: null, class: '' }; // No badge if no specific condition matches
});

const vehicleLink = computed(() => {
  const id = props.vehicle.id || 'unknown';
  const slug = props.vehicle.slug || 'vehicle';
  return `/vehicle-for-sale/${props.vehicle.stockid || id}/${slug}`;
});

const handleCardClick = (event: MouseEvent) => {
  // Check if the click target or its parent is a link or button to prevent quick view
  let target = event.target as HTMLElement | null;
  while (target && target !== event.currentTarget) {
    if (target.tagName === 'A' || target.tagName === 'BUTTON' || target.closest('.favorite-button')) {
      return; // Click was on an interactive element, do nothing here
    }
    target = target.parentElement;
  }
  if (props.vehicle.id) {
    openQuickView(props.vehicle.id);
  }
};

const formatPerWeek = (perweek: any): string => {
  let value: number;
  if (typeof perweek === 'number') value = perweek;
  else if (perweek && typeof perweek === 'object' && 'value' in perweek) {
    const perweekVal = Array.isArray(perweek.value) ? perweek.value[0] : perweek.value;
    value = Number(perweekVal) || 0;
  } else if (typeof perweek === 'string') value = Number(perweek) || 0;
  else value = 0;
  return Math.round(value).toString();
};

const formatKilometers = (kms: number | string | null | undefined): string => {
  if (kms === null || kms === undefined) return 'N/A';
  const numKms = Number(kms);
  if (isNaN(numKms)) return 'N/A';
  if (numKms < 100 && numKms > 0) return `${numKms} km`; // For very low kms like 15km
  if (numKms === 0) return '0 km';
  return `${numKms.toLocaleString()} km`;
};

const getFuelInfo = (vehicle: any): string => {
  const fuel = vehicle.fuel?.displayValue?.[0] || 'N/A';
  const engineType = vehicle.engineType || (fuel !== 'N/A' && !fuel.toLowerCase().includes('electric') ? 'ULP - Premium' : 'N/A'); // Default to ULP if not electric
  if (fuel === 'N/A' && engineType === 'N/A') return 'N/A';
  if (fuel === 'N/A') return engineType;
  if (engineType === 'N/A' || fuel.toLowerCase().includes('electric')) return fuel;
  return `${fuel} - ${engineType}`;
};

</script>

<style scoped>
.group:hover .text-slate-800, .group:hover .text-slate-600 {
  /* color: var(--color-primary-500); Example hover, adjust to your theme */
}
.dark .group:hover .dark\:text-white, .dark .group:hover .dark\:text-slate-400 {
 /* color: var(--color-primary-400); Example hover, adjust to your theme */
}

.vehicle-card-animation {
  opacity: 1; 
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid #e2e8f0; 
}

.dark .vehicle-card-animation {
  border: 1px solid #4b5563; /* Slightly lighter dark border */
}

.vehicle-card-animation:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px -3px rgba(0, 0, 0, 0.1), 0 4px 8px -2px rgba(0, 0, 0, 0.06);
}

img {
  transition: opacity 0.3s ease-in-out;
}

.aspect-w-16 {
    position: relative;
    padding-bottom: 56.25%;
}
.aspect-w-16 > * {
    position: absolute;
    height: 100%;
    width: 100%;
    left: 0;
    top: 0;
}

/* Ensure FavoriteButton has a nice circular background */
/* Targeting the button directly assuming FavoriteButton.vue structure */
:deep(.promo-card-favorite-button button) {
  background-color: white !important;
  border-radius: 9999px !important;
  padding: 0.5rem !important; /* p-2 */
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid rgba(0,0,0,0.05);
}
.dark :deep(.promo-card-favorite-button button) {
  background-color: #1f2937 !important; /* dark:bg-slate-800 approximation */
  border: 1px solid rgba(255,255,255,0.1);
}
:deep(.promo-card-favorite-button button:hover) {
 background-color: #f3f4f6 !important; /* gray-100 for light hover */
 box-shadow: 0 6px 8px -1px rgba(0,0,0,0.1), 0 3px 5px -1px rgba(0,0,0,0.06);
}
.dark :deep(.promo-card-favorite-button button:hover) {
 background-color: #374151 !important; /* slate-700 for dark hover */
}

:deep(.promo-card-favorite-button svg) {
  color: #ef4444; /* text-red-500 for the icon color */
}
:deep(.promo-card-favorite-button.is-favorited svg) { /* Assuming FavoriteButton adds .is-favorited */
  fill: #ef4444; /* Fill red when favorited */
}

/* Image loading transitions */
img {
  transition: opacity 0.3s ease-in-out;
}

.aspect-w-16 {
    position: relative;
    padding-bottom: 56.25%;
}
.aspect-w-16 > * {
    position: absolute;
    height: 100%;
    width: 100%;
    left: 0;
    top: 0;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.8;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .aspect-16\/9::before { /* This class might not be used anymore, check aspect-w-16 */
    padding-top: 66.67%; 
  }
}
</style> 